import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll } from 'framer-motion';

// Import the single, unified stylesheet
import './fnq.css';

//=================================================================//
// 💾 1. CONSOLIDATED DATABASE (Using the most detailed version)
//=================================================================//
const faqData = {
    "General": [
        { q: "What is the NOWNFC Platform?", a: "NOWNFC is a next-generation platform for creating, managing, and analyzing dynamic Near Field Communication (NFC) experiences, from smart tags to interactive campaigns." },
        { q: "Who is this platform for?", a: "It's designed for marketers, event organizers, retailers, and businesses of all sizes who want to bridge the physical and digital worlds with engaging NFC technology." },
        { q: "Is there a free trial available?", a: "Yes, we offer a 14-day, no-obligation free trial on all our plans. You can experience the full power of NOWNFC without any commitment." },
        { q: "What makes NOWNFC different from other tools?", a: "NOWNFC's key differentiator is its seamless integration of dynamic tag management with real-time analytics, all in an incredibly user-friendly interface." },
        { q: "What kind of ROI can I expect from NFC marketing?", a: "Return on investment varies by campaign, but our clients typically see significant increases in customer engagement, lead generation, and direct sales by creating a frictionless link between their physical products and digital content." },
        { q: "Do users need a special app to scan the NFC tags?", a: "No. Modern smartphones (most iPhones since the XS and Android phones) have built-in NFC readers. Users simply tap their phone to the tag, and the experience loads automatically in their web browser." },
    ],
    "Product": [
        { q: "What are the core features?", a: "Our platform includes a dynamic NFC link manager, a drag-and-drop landing page builder, detailed tap analytics, campaign organization tools, QR code generation, and team collaboration features." },
        { q: "Can I import my existing NFC tag data?", a: "Yes. We support bulk import of existing tag URLs and data via CSV files, allowing for a smooth transition to the NOWNFC platform." },
        { q: "How does the analytics work?", a: "Every time an NFC tag managed by our platform is tapped, we securely log the event, providing you with data on location, time, device type, unique vs. total taps, and user flow through your linked content." },
        { q: "Can I collaborate with my team in real-time?", a: "Yes, our Team and Enterprise plans allow multiple users to collaborate on campaigns, manage tags, and view analytics simultaneously with role-based permissions." },
        { q: "Can I schedule content changes for my NFC tags?", a: "Yes. You can schedule a tag's destination URL to change at a specific future date and time, perfect for limited-time offers or event-specific content." },
        { q: "What types of content can I link to?", a: "You can link to any web URL. This includes websites, videos, social media profiles, PDF documents, app store links, contact cards (vCards), and custom landing pages built with our editor." },
        { q: "Does the platform generate QR codes as well?", a: "Yes, for every dynamic link you create for an NFC tag, we automatically generate a corresponding dynamic QR code. This allows you to offer both tap and scan options for your campaigns." },
        { q: "What are 'Dynamic NFC Tags'?", a: "A dynamic tag points to a short URL managed by NOWNFC. This allows you to change the final destination URL in our dashboard at any time without having to physically re-program the NFC tag itself." },
    ],
    "Billing": [
        { q: "What are the subscription options?", a: "We offer monthly and annual plans tiered by the number of active tags and monthly taps. Annual plans include a significant discount (typically 2 months free)." },
        { q: "Can I cancel my subscription anytime?", a: "Yes, you can cancel or change your plan at any time from your account dashboard. You will retain access until the end of your current billing period." },
        { q: "What happens if I go over my monthly tap limit?", a: "We offer flexible overage protection. You'll be notified as you approach your limit, and you can choose to either upgrade your plan or pay a small per-tap overage fee. We never cut off service unexpectedly." },
        { q: "What payment methods do you accept?", a: "We accept all major credit cards, including Visa, Mastercard, and American Express. For Enterprise plans, we also support invoicing and bank transfers." },
        { q: "Can I upgrade or downgrade my plan?", a: "Yes, you can change your plan at any time. Upgrades are applied instantly, and downgrades take effect at the start of your next billing cycle." },
        { q: "Do you offer discounts for non-profits or educational institutions?", a: "Yes, we are proud to support non-profits and educational users with special discounted pricing. Please contact our sales team for more information." },
    ],
    "Security": [
        { q: "How is my data secured?", a: "We use end-to-end encryption for all your data, both in transit (TLS 1.3) and at rest (AES-256). Our infrastructure is built on world-class cloud providers and adheres to strict security protocols." },
        { q: "Do I own the content I create?", a: "You own 100% of the content and data associated with your campaigns. We are simply the platform that powers your experiences. Our privacy policy clearly outlines our role as a data processor." },
        { q: "Is the platform GDPR and CCPA compliant?", a: "Yes, we are fully compliant with GDPR, CCPA, and other major data privacy regulations. We provide tools and resources to help you maintain compliance in your own campaigns." },
        { q: "Can I enable Two-Factor Authentication (2FA) for my account?", a: "Yes, 2FA is available on all plans and can be enabled in your account security settings. We strongly recommend it for all users." },
        { q: "How do you prevent NFC tag cloning or unauthorized use?", a: "While standard NFC tags can be copied, our platform offers advanced security features on the Enterprise plan, such as unique tap authentication and server-side validation to mitigate unauthorized use for high-value applications." },
    ],
    "Integrations": [
        { q: "What apps can I connect with NOWNFC?", a: "We offer native integrations with popular tools like Google Analytics, HubSpot, Mailchimp, and Zapier to connect your NFC data to your wider marketing stack." },
        { q: "Do you have an API?", a: "Yes, a comprehensive REST API is available on our Enterprise plan, allowing you to build custom integrations and automate your NFC workflows, such as creating dynamic tags programmatically." },
        { q: "Can I pass data from the NFC tap to other systems?", a: "Yes, you can append custom parameters (like a user ID or source) to your destination URLs, which can then be captured by your analytics or marketing automation systems." },
        { q: "Is there a Shopify integration?", a: "Yes, our Shopify integration allows you to link NFC tags directly to product pages, add-to-cart links, or special discount URLs, perfect for smart packaging and retail displays." },
        { q: "What can I do with the Zapier integration?", a: "Our Zapier integration lets you connect NFC tap events to thousands of other apps. For example, you could add a lead to a Google Sheet, send a Slack notification, or create a new contact in your CRM every time a tag is tapped." },
    ],
    "Technical Support": [
        { q: "What type of NFC chips are compatible?", a: "Our platform is compatible with all standard NFC Forum Type 2-5 chips. For the best balance of compatibility and cost, we recommend NTAG213, NTAG215, or NTAG216 chips." },
        { q: "My phone isn't scanning the tag. What should I do?", a: "First, ensure NFC is enabled in your phone's settings. Second, locate your phone's NFC antenna (usually on the back near the top). Tap that specific area to the tag. Finally, make sure the tag isn't on a metal surface, which can interfere with the signal." },
        { q: "How close do I need to be to scan a tag?", a: "NFC stands for 'Near Field Communication.' The ideal scanning range is between direct contact and about 4 cm (1.5 inches)." },
        { q: "Can I write-protect my NFC tags?", a: "Yes, you can permanently lock an NFC tag after writing our dynamic URL to it. This prevents it from being overwritten. However, since our tags are dynamic, you don't need to lock them to prevent the destination from being changed." },
        { q: "What is the difference between NTAG213, NTAG215, and NTAG216?", a: "The main difference is memory capacity (NTAG213 < 215 < 216). For simply storing a URL from our platform, the cheapest NTAG213 chip is more than sufficient. Higher memory is only needed for storing large amounts of data directly on the chip, like a full vCard." },
        { q: "Do your NFC links work on all web browsers?", a: "Yes. Since the NFC tap simply opens a standard URL, the linked content will work on any modern web browser on both iOS and Android, including Chrome, Safari, Firefox, and Edge." },
    ]
};

const popularQuestionTitles = [
    "What is the NOWNFC Platform?",
    "Do users need a special app to scan the NFC tags?",
    "How does the analytics work?",
    "Can I schedule content changes for my NFC tags?",
    "What are 'Dynamic NFC Tags'?",
    "What happens if I go over my monthly tap limit?",
    "How is my data secured?",
    "Can I enable Two-Factor Authentication (2FA) for my account?",
    "Do you have an API?",
    "What can I do with the Zapier integration?",
    "My phone isn't scanning the tag. What should I do?",
    "What type of NFC chips are compatible?",
];

//=================================================================//
// 🧠 2. CUSTOM HOOKS
//=================================================================//
const useLocalStorage = (key, initialValue) => {
    const [storedValue, setStoredValue] = useState(() => {
        if (typeof window === "undefined") return initialValue;
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) { return initialValue; }
    });
    const setValue = value => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            if (typeof window !== "undefined") window.localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) { console.error(error); }
    };
    return [storedValue, setValue];
};


//=================================================================//
// 🧩 3. CHILD COMPONENTS (REBUILT & COMBINED)
//=================================================================//

// --- A clean header to introduce the entire page ---
const FAQHeader = () => (
    <header className="faq-header">
        <h1 className="faq-header-title">Frequently Asked Questions</h1>
        <p className="faq-header-subtitle">
            Find answers to your questions about our NFC platform. From general inquiries to technical details, we've got you covered.
        </p>
    </header>
);

// --- The visually engaging popular questions marquee ---
const PopularQuestions = ({ onQuestionSelect }) => {
    const scrollDuration = popularQuestionTitles.length * 4;

    return (
        <section className="popular-questions-section">
            <div className="popular-questions-scroller">
                <div className="popular-questions-track" style={{ animation: `marquee ${scrollDuration}s linear infinite` }}>
                    {[...popularQuestionTitles, ...popularQuestionTitles].map((q, index) => (
                        <button
                            key={`${q}-${index}`}
                            className="popular-question-card"
                            onClick={() => onQuestionSelect(q)}
                        >
                            <span className="popular-card-number">
                                {String(popularQuestionTitles.indexOf(q) + 1).padStart(2, '0')}
                            </span>
                            <p className="popular-card-text">{q}</p>
                            <div className="popular-card-arrow" aria-hidden="true">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
};


// --- The core FAQ accordion item for the main list ---
const FAQAccordionItem = React.forwardRef(({ faq, isOpen, onToggle }, ref) => {
    return (
        <div ref={ref} className="faq-item">
            <button className="faq-question-trigger" onClick={onToggle} aria-expanded={isOpen}>
                <span className="faq-question-text">{faq.q}</span>
                <div className="faq-icon-wrapper" aria-hidden="true">
                    <motion.div animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.3, ease: "easeOut" }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </motion.div>
                </div>
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.section
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { opacity: 1, height: 'auto', marginTop: '16px' },
                            collapsed: { opacity: 0, height: 0, marginTop: '0px' }
                        }}
                        transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                        className="faq-answer-panel"
                    >
                        <p className="faq-answer-content">{faq.a}</p>
                    </motion.section>
                )}
            </AnimatePresence>
        </div>
    );
});

// --- The enhanced "Still Have Questions?" CTA ---
const StillHaveQuestionsCTA = () => (
    <section className="still-questions-cta-section">
        <div className="still-questions-cta-container">
            <div className="still-questions-cta-text-content">
                <h2 className="still-questions-cta-title">Can't Find Your Answer?</h2>
                <p className="still-questions-cta-subtitle">Our team of experts is here to help you with anything you need, from campaign strategy to technical integration. Don't hesitate to reach out.</p>
                <a href='/contact' className="still-questions-cta-button">
                    Contact Our Experts
                </a>
            </div>
            <div className="still-questions-cta-icon-wrapper" aria-hidden="true">
                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/></svg>
            </div>
        </div>
    </section>
);


//=================================================================//
// 🚀 4. MAIN COMPONENT: THE FULL COMBINED EXPERIENCE
//=================================================================//
function CombinedFAQ() {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('General');
    const [openFAQs, setOpenFAQs] = useLocalStorage('nownfc-open-faqs-v4', []); // Use a new key for the new combined state
    const faqRefs = useRef({});
    const { scrollYProgress } = useScroll();

    const toggleFAQ = (question) => {
        setOpenFAQs(prev =>
            prev.includes(question)
                ? prev.filter(q => q !== question)
                : [...prev, question]
        );
    };

    const allQuestions = useMemo(() => Object.values(faqData).flat(), []);

    const questionToCategoryMap = useMemo(() => {
        const map = {};
        for (const category in faqData) {
            faqData[category].forEach(faq => { map[faq.q] = category; });
        }
        return map;
    }, []);

    const handlePopularQuestionSelect = (question) => {
        const category = questionToCategoryMap[question];
        if (!category) return;

        setSearchTerm('');
        setActiveCategory(category);

        if (!openFAQs.includes(question)) {
            setOpenFAQs(prev => [...new Set([...prev, question])]);
        }

        setTimeout(() => {
            const element = faqRefs.current[question];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('highlight-scroll');
                setTimeout(() => element?.classList.remove('highlight-scroll'), 2000);
            }
        }, 100);
    };

    const filteredFaqs = useMemo(() => {
        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            return allQuestions.filter(faq =>
                faq.q.toLowerCase().includes(lowerCaseSearch) || faq.a.toLowerCase().includes(lowerCaseSearch)
            );
        }
        return faqData[activeCategory] || [];
    }, [searchTerm, activeCategory, allQuestions]);

    const categories = Object.keys(faqData);

    return (
        <>
            <motion.div className="scroll-progress-bar" style={{ scaleX: scrollYProgress }} />
            <div className="faq-page-wrapper">
                
                <FAQHeader />
                <PopularQuestions onQuestionSelect={handlePopularQuestionSelect} />

                {/* This is the main, interactive FAQ list section */}
                <section id="faq-main-content" className="faq-main-section">
                    <div className="faq-content-container">
                        <h2 className="faq-list-title">Explore by Category</h2>
                        <div className="faq-controls">
                            <div className="search-bar-wrapper">
                                <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search all questions..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <AnimatePresence>
                                {!searchTerm && (
                                    <motion.nav
                                        className="category-tabs-container"
                                        aria-label="FAQ Categories"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="category-tabs">
                                            {categories.map(category => (
                                                <button
                                                    key={category}
                                                    onClick={() => setActiveCategory(category)}
                                                    className={`tab-button ${activeCategory === category ? 'active' : ''}`}
                                                >
                                                    {category}
                                                    {activeCategory === category && <motion.div className="active-tab-indicator" layoutId="activeTabIndicator" />}
                                                </button>
                                            ))}
                                        </div>
                                    </motion.nav>
                                )}
                            </AnimatePresence>
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={searchTerm ? 'search-results' : activeCategory}
                                className="faq-list"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {filteredFaqs.length > 0 ? (
                                    filteredFaqs.map(faq => (
                                        <FAQAccordionItem
                                            key={faq.q}
                                            ref={el => faqRefs.current[faq.q] = el}
                                            faq={faq}
                                            isOpen={openFAQs.includes(faq.q)}
                                            onToggle={() => toggleFAQ(faq.q)}
                                        />
                                    ))
                                ) : (
                                    <p className="no-results-message">No questions found. Try a different category or search term.</p>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </section>
                
                <StillHaveQuestionsCTA />
            </div>
        </>
    );
}

export default CombinedFAQ;